import type { FilterRelationalQuantifier, GlobalFieldId } from '#layers/data-model/public';
import type { FilterSchemaLookup } from './filter-path';
import type { FilterSchemaCollection } from './filter-schema';
import { isNonArrayRecord } from '#layers/base/public';
import { isFilterRelationalQuantifier, LogicalOperator, UnaryFilterOperator } from '#layers/data-model/public';

/** What a filter gate allows at a path of field ids from the root collection. */
export interface ClauseRules {
	allowsPath: (path: readonly GlobalFieldId[]) => boolean;
	allowsOperator: (path: readonly GlobalFieldId[], operator: string) => boolean;
	allowsQuantifier: (path: readonly GlobalFieldId[], quantifier: FilterRelationalQuantifier) => boolean;
	allowsLogical: (operator: LogicalOperator | UnaryFilterOperator, path: readonly GlobalFieldId[]) => boolean;
	/** Whether the filter object at `path` may hold several keys at once. */
	allowsSeveralKeys: (path: readonly GlobalFieldId[]) => boolean;
	/** Whether the field at `path` may carry several operators at once. */
	allowsSeveralOperators: (path: readonly GlobalFieldId[]) => boolean;
}

/** Whether every field, relation, quantifier, operator, and combinator in a clause is allowed, and each object holds no more keys than it may. */
export function isClauseAllowed(
	rules: ClauseRules,
	schema: Pick<FilterSchemaLookup, 'findFieldByApiName' | 'findOppositeCollectionByRelationFieldId'>,
	collection: FilterSchemaCollection,
	clause: unknown,
	path: GlobalFieldId[] = [],
): boolean {
	if (!isNonArrayRecord(clause)) return false;
	if (Object.keys(clause).length > 1 && !rules.allowsSeveralKeys(path)) return false;

	return Object.entries(clause).every(([key, value]) => {
		if (key === LogicalOperator.AND || key === LogicalOperator.OR) {
			return rules.allowsLogical(key, path) && Array.isArray(value) && value.every((operand) => isClauseAllowed(rules, schema, collection, operand, path));
		}

		if (key === UnaryFilterOperator.NOT) return rules.allowsLogical(key, path) && isClauseAllowed(rules, schema, collection, value, path);

		const field = schema.findFieldByApiName(collection, key);
		if (!field) return false;
		const fieldPath = [...path, field.id];

		if ('type' in field) {
			if (!isNonArrayRecord(value)) return false;
			const operators = Object.keys(value);
			if (operators.length > 1 && !rules.allowsSeveralOperators(fieldPath)) return false;
			return operators.every((operator) => rules.allowsOperator(fieldPath, operator));
		}

		const target = schema.findOppositeCollectionByRelationFieldId(field.id);
		if (!target || !isNonArrayRecord(value) || !rules.allowsPath(fieldPath)) return false;

		// A to-many traversal wraps the target clause in a quantifier.
		const quantifier = Object.keys(value).find((name): name is FilterRelationalQuantifier => isFilterRelationalQuantifier(name));
		if (quantifier) return rules.allowsQuantifier(fieldPath, quantifier) && isClauseAllowed(rules, schema, target, value[quantifier], fieldPath);

		return isClauseAllowed(rules, schema, target, value, fieldPath);
	});
}
